import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useRBAC } from "../contexts/RBACContext";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Send, Clock, Calendar, Shield, AlertTriangle, Settings, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function ChangeRequestDetail({ user }) {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useRBAC();
  const effectiveRole = user.role;

  const [changeRequest, setChangeRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignees, setAssignees] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: "",
    priority: "medium",
    impact_assessment: "medium",
    risk_level: "medium",
    assigned_to: null,
    assigned_to_name: null,
  });

  const canUpdate = hasPermission("ticket_update", effectiveRole);

  useEffect(() => {
    fetchChangeRequest();
    fetchAssignees();
  }, [requestId]);

  const fetchChangeRequest = async () => {
    try {
      const response = await axios.get(`${API}/change-requests/${requestId}`);
      setChangeRequest(response.data);
      setUpdateData({
        status: response.data.status || "pending",
        priority: response.data.priority || "medium",
        impact_assessment: response.data.impact_assessment || "medium",
        risk_level: response.data.risk_level || "medium",
        assigned_to: response.data.assigned_to || null,
        assigned_to_name: response.data.assigned_to_name || null,
      });
    } catch (e) {
      toast.error("Failed to load change request");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignees = async () => {
    try {
      const response = await axios.get(`${API}/tickets/assignees`);
      setAssignees(response.data);
    } catch (e) { /* non-critical */ }
  };

  const handleUpdate = async () => {
    try {
      await axios.patch(`${API}/change-requests/${requestId}`, updateData);
      toast.success("Change request updated");
      fetchChangeRequest();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    try {
      await axios.post(`${API}/change-requests/${requestId}/notes`, {
        content: noteContent,
        is_private: isPrivateNote,
      });
      toast.success("Note added");
      setNoteContent("");
      setIsPrivateNote(false);
      fetchChangeRequest();
    } catch (e) {
      toast.error("Failed to add note");
    }
  };

  const getPriorityColor = (p) => {
    const colors = { low: "bg-green-100 text-green-700", medium: "bg-yellow-100 text-yellow-700", high: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700" };
    return colors[p] || colors.medium;
  };

  const getStatusColor = (s) => {
    const colors = { pending: "bg-yellow-100 text-yellow-700", approved: "bg-blue-100 text-blue-700", in_progress: "bg-purple-100 text-purple-700", completed: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };
    return colors[s] || colors.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#1E1E1E]">
        <Navbar user={user} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      </div>
    );
  }

  if (!changeRequest) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#1E1E1E]">
        <Navbar user={user} />
        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <p className="text-slate-500">Change request not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/tickets")}>Back to Tickets</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#1E1E1E]">
      <Navbar user={user} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/tickets")} data-testid="back-to-tickets">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="font-mono text-lg font-bold text-slate-700 dark:text-white">{changeRequest.request_number}</span>
              <Badge className={getStatusColor(changeRequest.status)}>{changeRequest.status.replace("_", " ")}</Badge>
              <Badge className={getPriorityColor(changeRequest.priority)}>{changeRequest.priority}</Badge>
              <Badge variant="outline">{changeRequest.change_type}</Badge>
            </div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white mt-1">{changeRequest.title}</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details Card */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">Description</h3>
                  <p className="text-slate-700 dark:text-white whitespace-pre-wrap">{changeRequest.description}</p>
                </div>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">Justification / Reason</h3>
                  <p className="text-slate-700 dark:text-white whitespace-pre-wrap">{changeRequest.justification}</p>
                </div>
                {changeRequest.rollback_plan && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-1">Rollback Plan</h3>
                      <p className="text-slate-700 dark:text-white whitespace-pre-wrap">{changeRequest.rollback_plan}</p>
                    </div>
                  </>
                )}
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Planned Start</p>
                      <p className="font-medium text-slate-700 dark:text-white">{changeRequest.planned_start_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Planned End</p>
                      <p className="font-medium text-slate-700 dark:text-white">{changeRequest.planned_end_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Impact</p>
                      <Badge className={getPriorityColor(changeRequest.impact_assessment)}>{changeRequest.impact_assessment}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Risk Level</p>
                      <Badge className={getPriorityColor(changeRequest.risk_level)}>{changeRequest.risk_level}</Badge>
                    </div>
                  </div>
                </div>
                {changeRequest.systems_affected && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Systems / Services Affected</p>
                        <p className="text-sm font-medium text-slate-700 dark:text-white">{changeRequest.systems_affected}</p>
                      </div>
                    </div>
                  </>
                )}
                {changeRequest.dealerships && changeRequest.dealerships.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Dealerships</p>
                      <div className="flex flex-wrap gap-2">
                        {changeRequest.dealerships.map((d) => (
                          <Badge key={d} variant="outline">{d}</Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                <Separator />
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>Created by {changeRequest.created_by_name}</span>
                  <span>on {new Date(changeRequest.created_at).toLocaleDateString()}</span>
                  {changeRequest.assigned_to_name && <span>Assigned to {changeRequest.assigned_to_name}</span>}
                </div>
              </CardContent>
            </Card>

            {/* Activity Notes */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageSquare className="w-5 h-5" /> Activity Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {canUpdate && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Add a note..."
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      rows={3}
                      data-testid="change-note-input"
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                        <input type="checkbox" checked={isPrivateNote} onChange={(e) => setIsPrivateNote(e.target.checked)} className="rounded" />
                        Private note
                      </label>
                      <Button size="sm" onClick={handleAddNote} disabled={!noteContent.trim()} data-testid="submit-change-note">
                        <Send className="w-4 h-4 mr-1" /> Add Note
                      </Button>
                    </div>
                  </div>
                )}
                {changeRequest.activity_notes && changeRequest.activity_notes.length > 0 ? (
                  <div className="space-y-3">
                    {changeRequest.activity_notes.map((note) => (
                      <div key={note.id || note.created_at} className={`p-4 rounded-lg ${note.is_private ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700" : "bg-slate-50 dark:bg-slate-800"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-slate-800 dark:text-white">{note.user_name}</span>
                          <div className="flex items-center gap-2">
                            {note.is_private && <Badge className="bg-amber-100 text-amber-700 text-xs">Private</Badge>}
                            <span className="text-xs text-slate-500">{new Date(note.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-4 text-sm">No activity notes yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Update Form */}
          {canUpdate && (
            <div className="space-y-6">
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg">Update Request</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={updateData.status} onValueChange={(v) => setUpdateData(prev => ({ ...prev, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={updateData.priority} onValueChange={(v) => setUpdateData(prev => ({ ...prev, priority: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Impact</Label>
                    <Select value={updateData.impact_assessment} onValueChange={(v) => setUpdateData(prev => ({ ...prev, impact_assessment: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assigned To</Label>
                    <Select value={updateData.assigned_to || "unassigned"} onValueChange={(v) => {
                      const assignee = assignees.find(a => a.id === v);
                      setUpdateData(prev => ({
                        ...prev,
                        assigned_to: v === "unassigned" ? null : v,
                        assigned_to_name: assignee ? assignee.name : null
                      }));
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {assignees.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleUpdate} data-testid="update-change-request-btn">
                    Update Request
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
